import React from 'react';

import { Loader } from '@/components/Loader';
import { useAIBudgetStatus } from '@/hooks/useAIBudget';
import { AISpendLimitEditForm } from '@/sections/settings/components/forms/AISpendLimitEditForm';
import { SettingDisplayCard } from '@/sections/settings/components/SettingDisplayCard';
import { SettingEditableCard } from '@/sections/settings/components/SettingEditableCard';
import { useAuth } from '@/state/auth';
import { cn } from '@/utils/classname';

interface AISpendCardProps {
  email?: string;
}

const formatUSD = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const CONTENT_RIGHT_PADDING = 'pr-24';

/**
 * AI Spend section — Praetorian-only (gated at the parent). Mirrors
 * the visual cadence of the sibling toggle cards in
 * PraetorianFeaturesSection: title + one-line description. Adds a
 * thin progress bar when a limit is configured so spend-vs-cap is
 * legible at a glance.
 *
 * Bifrost only meters usage against an attached Budget; when none is
 * configured, current_usage is $0 and the bar is omitted.
 */
export const AISpendCard: React.FC<AISpendCardProps> = ({ email }) => {
  const { isPraetorianUser } = useAuth();
  const { data, isLoading, isError } = useAIBudgetStatus({ email });

  if (!isPraetorianUser) {
    return null;
  }

  if (isLoading) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-loading">
        <h4 className="font-bold">AI Spend</h4>
        <Loader isLoading type="spinner" />
      </SettingDisplayCard>
    );
  }

  if (isError) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-error">
        <h4 className="font-bold">AI Spend</h4>
        <span className="text-chariot-text-secondary text-xs">
          Failed to load AI spend.
        </span>
      </SettingDisplayCard>
    );
  }

  if (data === null) {
    return (
      <SettingDisplayCard data-testid="ai-spend-card-unprovisioned">
        <h4 className="font-bold">AI Spend</h4>
        <span className="text-chariot-text-secondary text-xs">
          Not provisioned yet.
        </span>
      </SettingDisplayCard>
    );
  }

  if (data === undefined) {
    return null;
  }

  const hasLimit = data.set;
  const percent =
    hasLimit && data.max_limit > 0
      ? Math.min(100, (data.current_usage / data.max_limit) * 100)
      : 0;
  const overLimit = hasLimit && data.current_usage > data.max_limit;

  return (
    <SettingEditableCard
      data-testid="ai-spend-card"
      editConfig={{
        modalTitle: 'Set AI Spend Limit',
        formComponent: AISpendLimitEditForm,
        formProps: {
          email,
          currentMaxLimit: hasLimit ? data.max_limit : undefined,
        },
        modalSize: 'lg',
        strategy: 'local-state',
      }}
    >
      <div className={cn('flex flex-col gap-2', CONTENT_RIGHT_PADDING)}>
        <h4 className="font-bold">AI Spend</h4>
        <div className="text-chariot-text-secondary text-xs">
          <span
            className={cn(
              'tabular-nums',
              overLimit
                ? 'text-chariot-text-red font-medium'
                : 'text-chariot-text-primary'
            )}
            data-testid="ai-spend-usage-value"
          >
            {formatUSD(data.current_usage)}
          </span>{' '}
          <span data-testid="ai-spend-limit-value">
            {hasLimit
              ? `of ${formatUSD(data.max_limit)} monthly`
              : '· no monthly limit'}
          </span>
          {hasLimit && (
            <>
              {' '}
              <span
                className={
                  overLimit ? 'text-chariot-text-red font-medium' : undefined
                }
                data-testid="ai-spend-percent"
              >
                · {percent.toFixed(0)}%
              </span>
            </>
          )}
        </div>
        {hasLimit && (
          <div
            className="bg-chariot-background-secondary h-1 w-full overflow-hidden rounded-full"
            role="progressbar"
            aria-valuenow={Math.round(percent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Spend ${percent.toFixed(0)}% of monthly limit`}
            data-testid="ai-spend-progress"
          >
            <div
              className={cn(
                'h-full rounded-full transition-[width] duration-300 ease-out',
                overLimit
                  ? 'bg-chariot-status-critical'
                  : 'bg-chariot-brand-primary'
              )}
              style={{ width: `${Math.max(2, percent)}%` }}
            />
          </div>
        )}
      </div>
    </SettingEditableCard>
  );
};
